import React, { useState, useMemo, useCallback, useEffect } from 'react';
import stockReorderApi, { errorMessage } from '../../../api/stockReorderApi';
import SupplierBlocks from './SupplierBlocks';
import ItemsTable from './ItemsTable';
import {
    money,
    num,
    filterItems,
    sortItems,
    coverValue,
    DEFAULT_SORT
} from '../reorderUtils';

const DEFAULT_PARAMS = { movementDays: 90, leadTimeDays: 30, safetyDays: 15 };

/**
 * Generate and review a reorder sheet.
 *
 * Single-branch mode can additionally take uploaded workbooks; multi-branch
 * mode always reads the file paths configured on each branch, because there is
 * no sensible way to attach one upload to several branches at once. That split
 * mirrors the two endpoints on the Python side (/generate vs /generate-multi).
 */
const ReorderTab = ({
    businessUnits,
    isAdmin,
    branchId,
    onBranchChange,
    sheet,
    snapshot,
    hasLive,
    editable,
    busyPart,
    onSheet,
    onBackToLive,
    onEdit,
    onReset,
    onExportSheet,
    onExportSupplier,
    onOrderCsv,
    canExport = false,
    onToast
}) => {
    const [mode, setMode] = useState('single');
    const [selectedIds, setSelectedIds] = useState([]);
    const [params, setParams] = useState(DEFAULT_PARAMS);

    const [stockFile, setStockFile] = useState(null);
    const [extraFiles, setExtraFiles] = useState([]);
    const [supplierFile, setSupplierFile] = useState(null);

    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    // Two values on purpose: the box updates instantly, the filter lags behind.
    // Filtering thousands of items on every keystroke made typing stutter.
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [onlyNeeded, setOnlyNeeded] = useState(false);
    const [onlyMultiSupplier, setOnlyMultiSupplier] = useState(false);
    const [onlyInternational, setOnlyInternational] = useState(false);
    const [sort, setSort] = useState(DEFAULT_SORT);
    const [collapsed, setCollapsed] = useState(() => new Set());

    // Grouped is the default because buying happens per supplier — "what do I
    // order from Alpha?" is the daily question. The flat table is for hunting a
    // part or auditing the whole sheet.
    const [view, setView] = useState('supplier');

    useEffect(() => {
        const id = setTimeout(() => setSearch(searchInput), 250);
        return () => clearTimeout(id);
    }, [searchInput]);

    const isMulti = mode === 'multi';
    const canRun = isMulti ? selectedIds.length > 0 : Boolean(branchId);
    const viewingSnapshot = Boolean(snapshot);

    const setParam = (key) => (e) => setParams((p) => ({ ...p, [key]: e.target.value }));

    const toggleSelected = (id) =>
        setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

    const toggleGroup = useCallback((supplier) => {
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(supplier)) next.delete(supplier);
            else next.add(supplier);
            return next;
        });
    }, []);

    const toggleSort = (key) =>
        setSort((s) => (s.key === key ? { key, asc: !s.asc } : { key, asc: true }));

    const run = useCallback(async () => {
        setBusy(true);
        setError('');
        setNotice('');
        try {
            const request = {
                isCombined: isMulti,
                params,
                businessUnitId: isMulti ? '' : branchId,
                selectedIds,
                files: { stockFile, extraFiles, supplierFile }
            };
            const data = isMulti
                ? await stockReorderApi.generateMulti(selectedIds, params)
                : await stockReorderApi.generate({
                      params,
                      businessUnitId: branchId,
                      files: request.files
                  });

            setCollapsed(new Set());
            onSheet({
                items: data.items || [],
                summary: data.summary,
                branches: data.branches || [],
                params: data.params,
                isCombined: isMulti,
                businessUnitId: isMulti ? '' : branchId,
                request
            });

            const where = isMulti ? ` across ${data.branches?.length || 0} branches` : '';
            onToast(
                `Sheet generated${where} — ${data.summary?.items_needing_reorder} of ${data.summary?.total_items} items need reorder.`
            );
            if (!data.items?.length) {
                setNotice('The sheet generated successfully but contains no items you can view.');
            }
        } catch (err) {
            setError(errorMessage(err, 'Could not generate the reorder sheet'));
        } finally {
            setBusy(false);
        }
    }, [
        isMulti,
        selectedIds,
        params,
        branchId,
        stockFile,
        extraFiles,
        supplierFile,
        onSheet,
        onToast
    ]);

    const save = useCallback(async () => {
        const label = window.prompt('Name this saved sheet (optional):', '');
        if (label === null) return;
        setBusy(true);
        setError('');
        try {
            await stockReorderApi.saveHistory({ ...sheet.request, label });
            onToast('Saved. Find it under “Saved Sheets”.');
        } catch (err) {
            setError(errorMessage(err, 'Could not save this sheet'));
        } finally {
            setBusy(false);
        }
    }, [sheet, onToast]);

    const items = sheet?.items || [];

    const visibleItems = useMemo(
        () =>
            filterItems(items, { search, onlyNeeded, onlyMultiSupplier, onlyInternational }),
        [items, search, onlyNeeded, onlyMultiSupplier, onlyInternational]
    );

    const conflictCount = useMemo(
        () => items.filter((i) => i.multiple_suppliers).length,
        [items]
    );

    const topPriority = useMemo(() => {
        const pool = items.filter(
            (i) => i.needs_reorder && (!onlyInternational || i.is_international_supplier)
        );
        return sortItems(pool, { key: 'reorder_value', asc: false }).slice(0, 10);
    }, [items, onlyInternational]);

    /**
     * Exports are unavailable only when the sheet can be reached by neither
     * route — no request to re-run from, and no stored id to build from. Until
     * /export-snapshot existed that covered every saved sheet and restored
     * draft, and the buttons stayed enabled while silently doing nothing.
     */
    const blockedReason = 'This sheet can’t be exported — generate it again first.';

    return (
        <div className="sr-stack">
            {viewingSnapshot && (
                <div className="sr-snapshot-banner">
                    <span>
                        Viewing saved sheet from{' '}
                        {new Date(snapshot.created_at).toLocaleString()}
                        {snapshot.label ? ` — ${snapshot.label}` : ''} (read-only)
                    </span>
                    <button className="sr-btn sr-btn-sm" onClick={onBackToLive}>
                        {hasLive ? 'Back to live data' : 'Clear'}
                    </button>
                </div>
            )}

            {/* ---------- Parameters ---------- */}
            <section className="sr-card">
                <h2 className="sr-card-title">Generate</h2>

                <div className="sr-mode-toggle">
                    <button
                        className={`sr-chip ${!isMulti ? 'active' : ''}`}
                        onClick={() => setMode('single')}
                    >
                        One branch
                    </button>
                    <button
                        className={`sr-chip ${isMulti ? 'active' : ''}`}
                        onClick={() => setMode('multi')}
                    >
                        Combine branches
                    </button>
                </div>

                {!isMulti ? (
                    <div className="sr-field">
                        <label htmlFor="sr-bu">Branch</label>
                        <select
                            id="sr-bu"
                            value={branchId}
                            onChange={(e) => onBranchChange(e.target.value)}
                        >
                            <option value="">Select a branch…</option>
                            {businessUnits.map((bu) => (
                                <option key={bu.id} value={bu.id}>
                                    {bu.name}
                                </option>
                            ))}
                        </select>
                        <span className="sr-hint">This branch is shared with Saved Sheets.</span>
                    </div>
                ) : (
                    <div className="sr-field">
                        <span className="sr-label">Branches</span>
                        <div className="sr-checklist">
                            {businessUnits.map((bu) => (
                                <label key={bu.id} className="sr-check">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.includes(bu.id)}
                                        onChange={() => toggleSelected(bu.id)}
                                    />
                                    {bu.name}
                                </label>
                            ))}
                            {businessUnits.length === 0 && (
                                <span className="sr-muted">No branches available.</span>
                            )}
                        </div>
                        <p className="sr-hint">
                            Combined runs read each branch's configured file paths — uploads apply
                            to single-branch runs only.
                        </p>
                    </div>
                )}

                <div className="sr-grid-3">
                    <div className="sr-field">
                        <label htmlFor="sr-movement">Movement period (days)</label>
                        <input
                            id="sr-movement"
                            type="number"
                            min="1"
                            value={params.movementDays}
                            onChange={setParam('movementDays')}
                        />
                        <span className="sr-hint">Window the usage rate is averaged over</span>
                    </div>
                    <div className="sr-field">
                        <label htmlFor="sr-lead">Lead time (days)</label>
                        <input
                            id="sr-lead"
                            type="number"
                            min="0"
                            value={params.leadTimeDays}
                            onChange={setParam('leadTimeDays')}
                        />
                        <span className="sr-hint">Supplier delivery time</span>
                    </div>
                    <div className="sr-field">
                        <label htmlFor="sr-safety">Safety stock (days)</label>
                        <input
                            id="sr-safety"
                            type="number"
                            min="0"
                            value={params.safetyDays}
                            onChange={setParam('safetyDays')}
                        />
                        <span className="sr-hint">Buffer on top of lead time</span>
                    </div>
                </div>

                {!isMulti && (
                    <details className="sr-details">
                        <summary>Upload files instead of using the branch's configured paths</summary>
                        <div className="sr-grid-3">
                            <div className="sr-field">
                                <label htmlFor="sr-stock-file">Stock movement file</label>
                                <input
                                    id="sr-stock-file"
                                    type="file"
                                    accept=".xls,.xlsx,.xlsm,.csv,.htm,.html"
                                    onChange={(e) => setStockFile(e.target.files[0] || null)}
                                />
                            </div>
                            <div className="sr-field">
                                <label htmlFor="sr-extra-files">Additional stock files to merge</label>
                                <input
                                    id="sr-extra-files"
                                    type="file"
                                    multiple
                                    accept=".xls,.xlsx,.xlsm,.csv,.htm,.html"
                                    onChange={(e) => setExtraFiles(Array.from(e.target.files || []))}
                                />
                            </div>
                            <div className="sr-field">
                                <label htmlFor="sr-supplier-file">Supplier mapping file</label>
                                <input
                                    id="sr-supplier-file"
                                    type="file"
                                    accept=".xls,.xlsx,.xlsm,.csv,.htm,.html"
                                    onChange={(e) => setSupplierFile(e.target.files[0] || null)}
                                />
                            </div>
                        </div>
                    </details>
                )}

                <div className="sr-actions">
                    <button className="sr-btn sr-btn-primary" onClick={run} disabled={busy || !canRun}>
                        {busy ? 'Working…' : 'Generate Order'}
                    </button>
                    {sheet && !sheet.isCombined && !viewingSnapshot && sheet.request && (
                        <button className="sr-btn" onClick={save} disabled={busy}>
                            Save sheet
                        </button>
                    )}
                    {sheet && (
                        <>
                            <button
                                className="sr-btn"
                                disabled={busy || !canExport}
                                title={canExport ? '' : blockedReason}
                                onClick={() => onExportSheet('excel')}
                            >
                                Export Excel
                            </button>
                            <button
                                className="sr-btn"
                                disabled={busy || !canExport}
                                title={canExport ? '' : blockedReason}
                                onClick={() => onExportSheet('pdf')}
                            >
                                Export PDF
                            </button>
                        </>
                    )}
                </div>

                {error && <div className="sr-alert sr-alert-error">{error}</div>}
                {notice && <div className="sr-alert sr-alert-info">{notice}</div>}
            </section>

            {/* ---------- Results ---------- */}
            {sheet && (
                <>
                    <section className="sr-card">
                        <h2 className="sr-card-title">Summary</h2>
                        <div className="sr-stats">
                            <div className="sr-stat">
                                <span className="sr-stat-value">{num(sheet.summary?.total_items)}</span>
                                <span className="sr-stat-label">Items</span>
                            </div>
                            <div className="sr-stat sr-stat-warn">
                                <span className="sr-stat-value">
                                    {num(sheet.summary?.items_needing_reorder)}
                                </span>
                                <span className="sr-stat-label">Need reorder</span>
                            </div>
                            <div className="sr-stat">
                                <span className="sr-stat-value">
                                    {money(sheet.summary?.total_reorder_value)}
                                </span>
                                <span className="sr-stat-label">Reorder value</span>
                            </div>
                        </div>
                        {sheet.branches?.length > 0 && (
                            <p className="sr-muted">Combined from: {sheet.branches.join(', ')}</p>
                        )}
                    </section>

                    {topPriority.length > 0 && (
                        <section className="sr-card">
                            <h2 className="sr-card-title">Top 10 by reorder value</h2>
                            <div className="sr-table-wrap">
                                <table className="sr-table">
                                    <thead>
                                        <tr>
                                            <th>Part #</th>
                                            <th>Description</th>
                                            <th>Supplier</th>
                                            <th className="sr-right">
                                                {onlyInternational ? 'Month Cover' : 'Days Cover'}
                                            </th>
                                            <th className="sr-right">Reorder Qty</th>
                                            <th className="sr-right">Reorder Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topPriority.map((item) => (
                                            <tr
                                                key={`${item.business_unit_id || ''}-${item.part_number}`}
                                                className="sr-row-flag"
                                            >
                                                <td>{item.part_number}</td>
                                                <td className="sr-wrap">
                                                    <span className="sr-desc" title={item.description}>
                                                        {item.description}
                                                    </span>
                                                </td>
                                                <td className="sr-wrap">
                                                    {item.supplier}
                                                    {sheet.isCombined && item.branch && (
                                                        <span className="sr-muted"> ({item.branch})</span>
                                                    )}
                                                </td>
                                                <td className="sr-right">
                                                    {coverValue(item, onlyInternational)}
                                                </td>
                                                <td className="sr-right">{num(item.reorder_qty)}</td>
                                                <td className="sr-right">{money(item.reorder_value)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )}

                    <section className="sr-card">
                        <div className="sr-card-head">
                            <h2 className="sr-card-title">
                                Items{' '}
                                <span className="sr-muted">
                                    ({num(visibleItems.length)} of {num(items.length)})
                                </span>
                            </h2>
                            {/* Two presentations of one sheet — same data, same
                                filters, same edits. */}
                            <div className="sr-segmented" role="group" aria-label="View">
                                <button
                                    type="button"
                                    className={view === 'supplier' ? 'active' : ''}
                                    aria-pressed={view === 'supplier'}
                                    onClick={() => setView('supplier')}
                                >
                                    By Supplier
                                </button>
                                <button
                                    type="button"
                                    className={view === 'spreadsheet' ? 'active' : ''}
                                    aria-pressed={view === 'spreadsheet'}
                                    onClick={() => setView('spreadsheet')}
                                >
                                    Spreadsheet
                                </button>
                            </div>
                        </div>

                        {conflictCount > 0 && (
                            <div className="sr-alert sr-alert-warn">
                                ⚠ {conflictCount} part{conflictCount === 1 ? '' : 's'} were mapped to
                                more than one supplier — hover the ⚠ on a row to see the alternates,
                                or{' '}
                                <button
                                    type="button"
                                    className="sr-link"
                                    onClick={() => setOnlyMultiSupplier(true)}
                                >
                                    show only these
                                </button>
                                .
                            </div>
                        )}

                        <div className="sr-filters">
                            <input
                                type="search"
                                placeholder="Search part, description or supplier…"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                            <label className="sr-check">
                                <input
                                    type="checkbox"
                                    checked={onlyNeeded}
                                    onChange={(e) => setOnlyNeeded(e.target.checked)}
                                />
                                Needs reorder only
                            </label>
                            <label className="sr-check">
                                <input
                                    type="checkbox"
                                    checked={onlyMultiSupplier}
                                    onChange={(e) => setOnlyMultiSupplier(e.target.checked)}
                                />
                                Multiple suppliers only
                            </label>
                            {isAdmin && (
                                <label className="sr-check">
                                    <input
                                        type="checkbox"
                                        checked={onlyInternational}
                                        onChange={(e) => setOnlyInternational(e.target.checked)}
                                    />
                                    International only
                                </label>
                            )}
                            {view === 'supplier' && (
                                <>
                                    <button
                                        className="sr-btn sr-btn-sm"
                                        onClick={() => setCollapsed(new Set())}
                                    >
                                        Expand all
                                    </button>
                                    <button
                                        className="sr-btn sr-btn-sm"
                                        onClick={() =>
                                            setCollapsed(
                                                new Set(
                                                    visibleItems.map((i) => i.supplier || 'Unmapped')
                                                )
                                            )
                                        }
                                    >
                                        Collapse all
                                    </button>
                                </>
                            )}
                        </div>

                        {/* The cells look like controls, but say it in words once
                            too — the sheet is 6,296 rows and nobody hovers a
                            cell to find out what it does. */}
                        {editable && (
                            <p className="sr-edit-hint">
                                <span className="sr-badge-yn yes editable" aria-hidden="true">
                                    Yes
                                    <span className="sr-caret">▾</span>
                                </span>
                                <span>
                                    and{' '}
                                    <span className="sr-editable-num" aria-hidden="true">
                                        627
                                    </span>{' '}
                                    cells are editable — click one to change it. Edited rows show ✎;
                                    press ↺ to restore the calculated value.
                                </span>
                            </p>
                        )}

                        {view === 'supplier' ? (
                            <SupplierBlocks
                                items={visibleItems}
                                sort={sort}
                                onSort={toggleSort}
                                isCombined={sheet.isCombined}
                                monthCover={onlyInternational}
                                collapsed={collapsed}
                                onToggle={toggleGroup}
                                editable={editable}
                                busyPart={busyPart}
                                onEdit={onEdit}
                                onReset={onReset}
                                fallbackBranchId={sheet.businessUnitId}
                                onExportSupplier={sheet.isCombined ? null : onExportSupplier}
                                onOrderCsv={sheet.isCombined ? null : onOrderCsv}
                                exportsBlocked={!canExport}
                                exportsBlockedReason={blockedReason}
                            />
                        ) : (
                            <ItemsTable
                                items={sortItems(visibleItems, sort)}
                                isCombined={sheet.isCombined}
                                monthCover={onlyInternational}
                                sort={sort}
                                onSort={toggleSort}
                                editable={editable}
                                busyPart={busyPart}
                                onEdit={onEdit}
                                onReset={onReset}
                                fallbackBranchId={sheet.businessUnitId}
                                showSupplier
                                virtualize
                                height="calc(100vh - 260px)"
                            />
                        )}
                    </section>
                </>
            )}
        </div>
    );
};

export default ReorderTab;
